import { getTotalValue } from '@nile/banyan/src/Pebbles/tax';
import {
  getDragHandleColumnProps,
  getTaxLabel,
  reorderItems
} from '@nile/client_management/src/Pages/Invoice/Utils/index';
import {
  getAccessor,
  getType,
  getWidth,
  isBold,
  isEditable as isEditableHelper,
  isFilterable
} from '@nile/client_management/src/Pebbles/InvoiceDetails';
import {
  getHeaderValue,
  getTranslatedText,
  PSA_CONSTANTS,
  QUOTE_STATUS,
  toast,
  INVOICE_STATUS,
  BackgroundIcon,
  Column,
  Loader,
  B1
} from '@nile/components';
import {
  sanitizeWhiteSpace,
  dompurifier,
  downloadHack,
  roundToFixed,
  getEntityAttributes
} from '@nile/components/src/Utils/helpers';
import { cloneDeep, isEqual, omit, includes } from 'lodash';
import envConfig from '@nile/home/src/Config/env';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { AccountClient } from '@nile/graphql';
import { GET_AVAILABLE_CREDITS } from '@nile/graphql/src/Query/client_management/partialPayments';
import BigNumber from 'bignumber.js';
import { useIntl, FormattedMessage } from 'react-intl';
import ActiveIntegrationIcon from '@nile/client_management/src/Components/ActiveInvoiceBillingIntegrationIcon';

const ROUND_OFF_LIMIT = 2;

export const getSubTotal = (items) => {
  let sTotal = 0;
  (items || []).forEach((el) => {
    sTotal += Number(el.amount);
  });
  return sTotal;
};

export const getTaxes = (data) => {
  let totalTax = 0;

  if (data.taxes) {
    data.taxes.forEach((tax) => {
      let taxAmount = 0;
      data.items.forEach((item) => {
        if (
          item.tax &&
          item.tax.rates.some((r) => r.rateId === tax?.rate?.rateId) &&
          item.taxable
        ) {
          const taxRateValue = new BigNumber(tax.taxRate).dividedBy(100);
          const lineItemTaxAmount = new BigNumber(item.amount)
            .multipliedBy(taxRateValue)
            .toNumber();
          taxAmount += roundToFixed(lineItemTaxAmount, ROUND_OFF_LIMIT);
        }
      });
      totalTax += taxAmount;
    });
  }

  return roundToFixed(totalTax, ROUND_OFF_LIMIT);
};

export const generateInvoiceOrQuoteSummaryRows = ({
  data,
  editMode = true,
  intl,
  isXeroIntegrated = false,
  integrationsLoading = false,
  integrationsError = null,
  isSiteWiseSplitEnabled = false,
  isDetailsPage = false,
  isAdditionalDiscountPercent
}) => {
  const rows = [];

  // Calculate main values
  const subTotal = getSubTotal(data?.items);
  const taxRates = getTaxes(data);
  const additionalDiscount = getAdditionalDiscount(
    subTotal,
    taxRates,
    data,
    isAdditionalDiscountPercent
  );

  const grandTotal = subTotal + taxRates - additionalDiscount;

  // Process taxes
  if (data.taxes?.length) {
    data.taxes.forEach((el, idx) => {
      rows.push({
        name: 'tax',
        prefix:
          idx === 0
            ? getTranslatedText(intl, 'invoice.details.list.taxCalculation')
            : '',
        rate: el.taxRate,
        label: getTaxLabel({
          taxName: el.tax.name,
          rateName: el?.rate?.name
        }),
        value: el.taxAmount,
        type: idx === 0 ? 'Tag' : ''
      });
    });
  }

  // Process additional discount
  if (
    editMode &&
    !integrationsLoading &&
    !integrationsError &&
    !isXeroIntegrated
  ) {
    if (
      data?.additionalDiscountRate !== null ||
      data?.additionalDiscount !== null
    ) {
      rows.push({
        name: 'additionalDiscountEdit',
        prefix: '',
        rate: '',
        label: 'Additional discount',
        // label: getTranslatedText(intl, 'invoice.details.list.additionalDiscount'),
        value: additionalDiscount,
        type: ''
      });
    } else {
      rows.push({
        name: 'addAdditionalDiscount',
        prefix: '',
        rate: '',
        label: 'Add additional discount',
        // label: getTranslatedText(intl, 'invoice.details.list.addAdditionalDiscount'),
        value: '',
        type: ''
      });
    }
  } else if (
    isDetailsPage &&
    (data?.additionalDiscountRate || data?.additionalDiscount)
  ) {
    rows.push({
      name: 'additionalDiscount',
      prefix: '',
      rate: '',
      label: 'Additional discount',
      // label: getTranslatedText(intl, 'invoice.details.list.additionalDiscount'),
      value: additionalDiscount,
      type: ''
    });
  }

  // Add subtotal if we have either discount or tax
  if (data.discountAmount || data.taxAmount) {
    rows.unshift({
      name: 'subTotal',
      prefix: '',
      rate: '',
      label: getTranslatedText(
        intl,
        isSiteWiseSplitEnabled
          ? 'invoice.details.list.overallSubTotal'
          : 'invoice.details.list.subTotal'
      ),
      value: subTotal.toString()
    });
  }

  return {
    rows,
    subTotal,
    taxRates,
    additionalDiscount,
    grandTotal
  };
};

export function updateProperties(args, dataRef, setData, invoiceSettings) {
  const {
    key,
    value,
    field,
    meta,
    properties,
    itemProperties,
    miscProperty,
    miscItem
  } = args;
  let modifiedData = cloneDeep(dataRef.current);
  const propertiesToUpdate = cloneDeep(properties || {});
  if (propertiesToUpdate.items?.length) {
    propertiesToUpdate.items = propertiesToUpdate.items.map((el, i) => ({
      ...el,
      itemOrder: el.itemOrder ?? i + 1
    }));
  }
  modifiedData = {
    ...modifiedData,
    ...propertiesToUpdate
  };
  if (properties?.items?.length) {
    // calculate tax for each item if it has tax.
    // else if sales tax(client level) is there, apply that tax.
    modifiedData.items.forEach((el, idx) => {
      if (el.tax) {
        el.tax.rates.forEach((rate) => {
          const isTaxPresent = modifiedData.taxes.some(
            (tax) => tax?.rate?.rateId === rate.rateId
          );
          if (!isTaxPresent) {
            modifiedData.taxes.push({
              rate: rate,
              tax: el.tax,
              taxRate: rate?.rateValue
            });
          }
        });
      } else if (el.taxable) {
        if (invoiceSettings?.taxable && invoiceSettings?.salesTax) {
          modifiedData.items[idx].tax = invoiceSettings.salesTax;
          invoiceSettings.salesTax.rates.forEach((rate) => {
            const isDefaultTaxPresent = modifiedData.taxes.some(
              (tax) => tax?.rate?.rateId === rate.rateId
            );
            if (!isDefaultTaxPresent) {
              modifiedData.taxes.push({
                rate: rate,
                tax: invoiceSettings.salesTax,
                taxRate: rate?.rateValue
              });
            }
          });
        }
      }
    });
    // calculate the taxAmount for each item in tax array by calculating tax for each item and adding to
    // that particular tax in taxes array.
    modifiedData.taxes.forEach((tax, idx) => {
      let taxAmount = 0;
      modifiedData.items.forEach((item) => {
        if (
          item.tax &&
          item.tax.rates.some((r) => r.rateId === tax.rate.rateId) &&
          item.taxable
        ) {
          taxAmount += Number((item.amount * Number(tax.taxRate)) / 100);
        }
      });
      modifiedData.taxes[idx].taxAmount = String(taxAmount);
    });
  }
  setData(modifiedData);
}

export const calculateData = (arg, config = {}) => {
  const ROUND_OFF_LIMIT = 2;

  const {
    modifiedData,
    key,
    value,
    field,
    meta,
    properties,
    itemProperties,
    miscProperty,
    miscItem
  } = arg;

  const {
    isSiteWiseSplitEnabled = false,
    isGroupByCategoryEnabled = false
  } = config;

  // Handle direct property updates
  if (properties) {
    const updatedModifiedData = {
      ...modifiedData,
      ...properties
    };
    return updatedModifiedData;
  }

  // Find item index
  const itemIndex = modifiedData.items.findIndex(
    (el) => el.itemId === field?.itemId
  );

  // Handle item property updates
  if (itemProperties) {
    modifiedData.items[itemIndex] = {
      ...modifiedData.items[itemIndex],
      ...itemProperties
    };
  } else if (key) {
    modifiedData.items[itemIndex] = {
      ...modifiedData.items[itemIndex],
      [key]: value
    };
  }

  // Handle item deletion
  if (miscProperty === 'delete') {
    const idx = modifiedData.items.findIndex(
      (el) => el.itemId === miscItem.itemId
    );
    modifiedData.items.splice(idx, 1);

    // Reorder items if site-wise split is not enabled
    if (!isSiteWiseSplitEnabled && !isGroupByCategoryEnabled) {
      modifiedData.items = reorderItems({
        newOrderItems: modifiedData.items
      });
    }
  }

  // Handle client tax changes
  if (miscProperty === 'clientTax') {
    modifiedData.items.forEach((lineItem, idx) => {
      modifiedData.items[idx] = {
        ...modifiedData.items[idx],
        tax: value
      };
    });
    modifiedData.taxes = [{ tax: value, taxRate: getTotalValue(value.rates) }];
  }

  // Handle tax related updates
  if (key === 'tax') {
    modifiedData.items[itemIndex].taxable = true;
  }

  if (key === 'taxable') {
    modifiedData.items[itemIndex].tax = value
      ? modifiedData.taxes?.[0]?.tax || null
      : null;
  }

  // Handle service catalog item changes
  if (key === 'serviceCatalogItem') {
    if (modifiedData.items[itemIndex]?.tax?.taxId !== meta?.tax?.taxId) {
      modifiedData.items[itemIndex].tax = meta?.tax;
    }
    modifiedData.items[itemIndex].taxable = meta?.taxable;
  }

  // Calculate line item amounts
  calculateLineItemAmounts(modifiedData, itemIndex);

  // Calculate totals
  calculateTotals(modifiedData);

  // Handle tax calculations
  if (key === 'tax' || key === 'serviceCatalogItem') {
    updateTaxes(modifiedData, value, key);
  }

  // Calculate final tax amounts
  calculateTaxAmounts(modifiedData, ROUND_OFF_LIMIT);

  return modifiedData;
};

// Helper functions

function calculateLineItemAmounts(modifiedData, itemIndex) {
  if (itemIndex > -1) {
    let lineItemDiscount = 0;
    const item = modifiedData.items[itemIndex];

    if (item.discountRate) {
      lineItemDiscount =
        (Number(item.quantity) *
          Number(item.unitPrice) *
          Number(item.discountRate)) /
        100;
    } else if (item.discountAmount) {
      lineItemDiscount = Number(item.discountAmount);
    }

    item.amount =
      Number(item.quantity) * Number(item.unitPrice) - lineItemDiscount;
  }
}

function calculateTotals(modifiedData) {
  let totalAmount = 0;
  const additionalDiscount = modifiedData?.additionalDiscount || 0;

  // Handle additional discount
  if (modifiedData.additionalDiscount && modifiedData.additionalDiscountRate) {
    totalAmount -= Number(additionalDiscount);
  }

  // Calculate total amount
  modifiedData.items.forEach((el) => {
    totalAmount += Number(el.amount);
  });
  modifiedData.totalAmount = String(totalAmount);

  // Calculate discount amount
  let discountAmount = 0;
  modifiedData.items.forEach((el) => {
    let eachItemDiscount = 0;
    if (el.discountRate) {
      eachItemDiscount =
        (Number(el.quantity) * Number(el.unitPrice) * Number(el.discountRate)) /
        100;
    } else if (el.discountAmount) {
      eachItemDiscount = Number(el.discountAmount);
    }
    discountAmount += eachItemDiscount;
  });
  modifiedData.discountAmount = String(discountAmount);
}

function updateTaxes(modifiedData, value, key) {
  const taxToConsider = value?.salesTax ? value.salesTax : value;

  if (taxToConsider?.rates) {
    taxToConsider.rates.forEach((rate) => {
      const isTaxPresent = modifiedData.taxes.some(
        (tax) => tax?.rate?.rateId === rate.rateId
      );
      if (!isTaxPresent) {
        modifiedData.taxes.push({
          rate: rate,
          tax: taxToConsider,
          taxRate: rate?.rateValue
        });
      }
    });
  }

  modifiedData.taxes = modifiedData.taxes.filter((t) => {
    return modifiedData.items?.some(
      (it) =>
        it.tax &&
        it.tax.rates.some((r) => r.rateId === t.rate.rateId) &&
        it.taxable
    );
  });
}

function calculateTaxAmounts(modifiedData, ROUND_OFF_LIMIT) {
  let totalTax = 0;

  modifiedData.taxes.forEach((tax, idx) => {
    let taxAmount = 0;
    modifiedData.items.forEach((item) => {
      if (
        item.tax &&
        item.tax.rates.some((r) => r.rateId === tax.rate.rateId) &&
        item.taxable
      ) {
        const taxRateValue = Number(tax.taxRate) / 100;
        const lineItemTaxAmount = Number(item.amount) * taxRateValue;
        taxAmount += roundToFixed(lineItemTaxAmount, ROUND_OFF_LIMIT);
      }
    });
    modifiedData.taxes[idx].taxAmount = String(taxAmount);
    totalTax += taxAmount;
  });

  modifiedData.totalAmount =
    Number(modifiedData.totalAmount) + Number(parseFloat(totalTax));
}

function getColumns({
  includeWorklogDate = false,
  tableConfig = {},
  editModeRef,
  editMode = false,
  clientId,
  currencyCode,
  countryCode,
  data,
  onChange,
  intl
}) {
  const cols = [
    includeWorklogDate && {
      name: 'worklogDate',
      label: getTranslatedText(intl, 'invoice.details.list.date')
    },
    {
      name: 'itemDetails',
      label: getTranslatedText(intl, 'invoice.details.list.itemDetails')
    },
    {
      name: 'details',
      label: getTranslatedText(intl, 'invoice.details.list.details')
    },
    {
      name: 'quantity',
      label: getTranslatedText(intl, 'invoice.details.list.quantityOrHours')
    },
    {
      name: 'unitPrice',
      label: getTranslatedText(intl, 'invoice.details.list.unitPrice')
    },
    tableConfig.discountRate && {
      name: 'discountRate',
      label: getTranslatedText(intl, 'invoice.details.list.discount')
    },
    tableConfig.tax && {
      name: 'tax',
      label: getTranslatedText(intl, 'invoice.details.list.tax')
    },
    {
      name: 'amount',
      label: getTranslatedText(intl, 'invoice.details.list.total')
    }
  ].filter(Boolean);

  return cols.map((col) => {
    return {
      Header: col.label,
      accessor: getAccessor(col, true),
      width: getWidth(col),
      type: getType(col),
      id: col.name,
      disableResizing: false,
      className: 'sticky',
      disableSortBy: true,
      editable: isEditableHelper(col),
      dataType: col?.name === 'worklogDate' ? 'date' : '',
      disableFilters: !isFilterable(col),
      actions: {
        onChange
      },
      field: col,
      allData: data || {},
      isBold: isBold(col),
      alignRight: col.name === 'amount',
      suffix: col.name === 'discountRate' ? '%' : '',
      editMode: editModeRef?.current || editMode,
      clientId,
      currencyCode,
      countryCode
    };
  });
}

export const configureTableColumns = ({
  intl,
  tableConfig = {},
  data = {},
  onChange,
  currencyCode = 'USD',
  countryCode = 'US',
  editModeRef,
  editMode = false,
  includeWorklogDate = false,
  isQuote = false
}) => {
  const columns = getColumns({
    includeWorklogDate,
    tableConfig,
    editModeRef,
    editMode,
    clientId: data?.client?.accountId,
    currencyCode,
    countryCode,
    data,
    onChange,
    intl
  });

  // Add delete action column if document is in draft status or edit mode
  const isEditable =
    editMode ||
    editModeRef?.current ||
    (isQuote && data?.statusEnum === QUOTE_STATUS.DRAFT?.value);

  if (isEditable) {
    columns.push({
      Header: ' ',
      accessor: 'name',
      width: 50,
      minWidth: 50,
      type: 'ItemDelete',
      id: 'action',
      disableResizing: false,
      className: 'sticky',
      disableSortBy: true,
      editable: false,
      disableFilters: true,
      actions: {
        onChange
      },
      field: null,
      allData: data || {},
      editMode: editModeRef?.current || editMode,
      clientId: data?.client?.accountId
    });
  }

  // Add drag handle column
  columns.unshift(
    getDragHandleColumnProps({
      data,
      clientId: data?.client?.accountId,
      editMode: isEditable
    })
  );

  return [{ Header: 'Name', columns }];
};

// Calculate additional discount based on discountRate
export const getAdditionalDiscount = (
  sTotal,
  tRates,
  data,
  isAdditionalDiscountPercent
) => {
  let discount = 0;

  if (!data.additionalDiscountRate && !data.additionalDiscount) {
    return discount;
  }

  if (isAdditionalDiscountPercent) {
    discount = ((sTotal + tRates) * Number(data.additionalDiscountRate)) / 100;
  } else {
    discount = data.additionalDiscount;
  }

  discount = roundToFixed(parseFloat(discount), 2);
  return discount;
};

export function checkRowHighlightHelper(rowToCompare, manipulatedData) {
  const itemToCompare = manipulatedData?.items?.find(
    (el) => el.itemId === rowToCompare?.original?.itemId
  );
  return !isEqual(
    omit(itemToCompare, 'itemOrder'),
    omit(rowToCompare?.original, 'itemOrder')
  );
}

export const usePdfDownloader = ({ downloadDetails, filePrefix, id }) => {
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const loaderTimeoutRef = useRef(null);
  const intl = useIntl();

  useEffect(() => {
    return () => {
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Handles downloading a PDF document
   */
  const onPdfDownloadClick = async () => {
    setPdfDownloadLoading(true);

    const details =
      typeof downloadDetails === 'function'
        ? downloadDetails()
        : downloadDetails;

    const requestOptions = {
      method: 'GET',
      credentials: 'include',
      headers: getHeaderValue()
    };

    if (loaderTimeoutRef.current) {
      clearTimeout(loaderTimeoutRef.current);
    }

    loaderTimeoutRef.current = setTimeout(() => {
      toast('warning', getTranslatedText(intl, 'invoice.pdf.download.time'));
    }, 1000);

    try {
      const response = await fetch(details, requestOptions);
      const data = await response.json();

      setPdfDownloadLoading(false);

      if (data?.errors) {
        toast(
          'error',
          getTranslatedText(
            intl,
            `app.errorCode.${data.errors?.[0]?.errorCode}`
          )
        );
      }
    } catch (error) {
      // If there's an error, attempt to download directly using the download hack
      setPdfDownloadLoading(false);
      downloadHack({
        href: details.href,
        downloadFileName: `${filePrefix}_${id}.pdf`
      });
    }
  };

  return {
    pdfDownloadLoading,
    setPdfDownloadLoading,
    onPdfDownloadClick
  };
};

// Typography helpers for Invoice/Quote
export const INVOICE_GOOGLE_FONT_MAP = {
  DEFAULT: { family: 'inherit', google: null },
  TIMES_NEW_ROMAN: {
    family: 'Times New Roman, Tinos, Times, serif',
    google: 'Tinos'
  },
  COURIER_NEW: {
    family: 'Courier New, Courier Prime, Courier, monospace',
    google: 'Courier Prime'
  }
};

export function useInvoiceTypography(typography) {
  const [fontLoaded, setFontLoaded] = useState(false);

  useEffect(() => {
    setFontLoaded(false);
    const key = (typography || 'DEFAULT').toUpperCase();
    const config =
      INVOICE_GOOGLE_FONT_MAP[key] || INVOICE_GOOGLE_FONT_MAP.DEFAULT;
    if (!config || !config.google) {
      setFontLoaded(true);
      return;
    }
    const fontId = `invoice-font-${config.google
      .replace(/\s+/g, '-')
      .toLowerCase()}`;
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
        config.google
      )}:wght@400;500;600;700&display=swap`;
      link.onload = () => setFontLoaded(true);
      link.onerror = () => setFontLoaded(true);
      document.head.appendChild(link);
    } else {
      setFontLoaded(true);
    }
  }, [typography]);

  const key = (typography || 'DEFAULT').toUpperCase();
  const { family } =
    INVOICE_GOOGLE_FONT_MAP[key] || INVOICE_GOOGLE_FONT_MAP.DEFAULT;

  return {
    fontLoaded,
    fontFamily: family,
    fontStyles: {
      fontFamily: family,
      '--custom-invoice-font': family
    },
    className: 'invoice-custom-font'
  };
}

export function onRowItemDragEnd({
  newOrderItems,
  isSiteWiseSplitEnabled = false,
  isGroupByCategoryEnabled = false,
  dataRef
}) {
  const modifiedData = cloneDeep(dataRef.current);

  if (!isSiteWiseSplitEnabled && !isGroupByCategoryEnabled) {
    modifiedData.items = reorderItems({ newOrderItems });
    return modifiedData;
  }

  newOrderItems.forEach((item, newitemIndex) => {
    modifiedData.items = modifiedData.items.map((modifiedItem) => {
      if (item.itemId === modifiedItem.itemId) {
        return {
          ...modifiedItem,
          itemOrder: newitemIndex + 1
        };
      }
      return modifiedItem;
    });
  });

  return modifiedData;
}
export function getRemainingBalance({
  invoiceTotal,
  amountPaid,
  totalAmountPaid
}) {
  const bigInvoiceTotal = new BigNumber(invoiceTotal);
  const bigAmountPaid = new BigNumber(amountPaid);
  const bigTotalAmountPaid = new BigNumber(totalAmountPaid || 0);
  const bigAmount = bigAmountPaid.plus(bigTotalAmountPaid);

  const difference = bigInvoiceTotal.minus(bigAmount).abs();
  const roundedDifference = roundToFixed(difference, 2);

  const isExcess = bigAmount.isGreaterThan(bigInvoiceTotal);

  let REMAINING_AMOUNT_STATUS;
  if (roundedDifference === 0) {
    REMAINING_AMOUNT_STATUS = PSA_CONSTANTS.NEUTRAL;
  } else if (isExcess) {
    REMAINING_AMOUNT_STATUS = PSA_CONSTANTS.POSITIVE;
  } else {
    REMAINING_AMOUNT_STATUS = PSA_CONSTANTS.NEGATIVE;
  }

  return {
    remainingBalance: roundedDifference,
    isExcess,
    remainingAmountStatus: REMAINING_AMOUNT_STATUS,
    amountPaidAfterBreakdown: roundToFixed(bigAmount, 2)
  };
}

export function hasCredits(creditAmount) {
  const aBigNumber = new BigNumber(creditAmount);

  return aBigNumber.isGreaterThan(0);
}

export function useCredits(clientId, restrictActions = false) {
  const { data, loading, refetch } = useQuery(GET_AVAILABLE_CREDITS, {
    variables: {
      input: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: 'client.accountId',
            operator: 'is',
            value: clientId
          }
        ]
      }
    },
    client: AccountClient,
    fetchPolicy: 'no-cache',
    skip: !clientId || restrictActions
  });

  if (restrictActions) {
    return {
      hasCredits: false,
      availableCredits: 0,
      isLoading: false,
      refetchCredits: () => {}
    };
  }

  return {
    hasCredits: hasCredits(data?.getAvailableCredits || 0) && !loading,
    availableCredits: parseFloat(data?.getAvailableCredits ?? '0'),
    isLoading: loading,
    refetchCredits: refetch
  };
}

/**
 * Formats a decimal value to a specified number of decimal places using BigNumber
 * with ROUND_HALF_UP rounding strategy
 * @param {number|string} value - The value to format
 * @param {number} decimalPlaces - Number of decimal places (default: 2)
 * @returns {number} - Formatted number with specified decimal places
 */
export function formatDecimalWithBigNumber(value, decimalPlaces = 2) {
  return new BigNumber(value || 0)
    .decimalPlaces(decimalPlaces, BigNumber.ROUND_HALF_UP)
    .toNumber();
}

export function Stamp({ statusEnum, partiallyPaid }) {
  if (statusEnum === INVOICE_STATUS.OVERDUE.value && !partiallyPaid) {
    return null;
  }

  return (
    includes(
      [
        INVOICE_STATUS.PAID.value,
        INVOICE_STATUS.VOID.value,
        INVOICE_STATUS.PARTIALLY_PAID.value,
        INVOICE_STATUS.OVERDUE.value
      ],
      statusEnum
    ) && (
      <div>
        <BackgroundIcon
          className="mr-10"
          url={`url(${envConfig.imageRoot}svg/${(partiallyPaid
            ? INVOICE_STATUS.PARTIALLY_PAID.value
            : statusEnum
          ).toLowerCase()}.svg)`}
          height="70px"
          width="70px"
        />
      </div>
    )
  );
}

export function SanitizedAddress({ address, header, alignRight }) {
  if (!address) return null;

  return (
    <Column gap={5} className={alignRight ? 'ta-r' : ''}>
      {header}
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: dompurifier(sanitizeWhiteSpace(address))
        }}
      />
    </Column>
  );
}

export function InvoiceCustomFields({
  fieldSettings,
  inactiveFields,
  editMode,
  loading,
  generateFieldData
}) {
  if (loading) {
    return <Loader />;
  }

  return (fieldSettings || [])?.map((field) => {
    const renderField = generateFieldData({
      editmode: editMode,
      key: field?.fieldName,
      field: field,
      entity: field?.entity,
      isInactive: inactiveFields?.includes(field?.fieldName)
    });
    return (
      renderField && (
        <div key={`${field?.fieldName}-${field?.entity}`}>{renderField}</div>
      )
    );
  });
}

export function ActiveIntegrationId({ integrationAppAndInvoiceId }) {
  if (!integrationAppAndInvoiceId?.length) return null;

  const invoiceId =
    integrationAppAndInvoiceId[1] || integrationAppAndInvoiceId[0];

  return (
    <span className="flex actr mb25 f-20">
      <ActiveIntegrationIcon
        integrationId={
          integrationAppAndInvoiceId[1] && integrationAppAndInvoiceId[0]
        }
      />
      <div className="ml-15 f-20">
        <B1>{`Invoice #${invoiceId}`}</B1>
      </div>
    </span>
  );
}

export const WHITE_TEXT_COLOR = 'var(--sops-app-nav-text-color)';
export const DARK_TEXT_COLOR = 'var(--sops-black-to-black)';

export function accentDecider(accent, isDarkTheme) {
  let accentBgColor;
  let accentTextColor;

  if (accent && accent.enabled) {
    accentBgColor = `var(${accent.color.cssVariable})`;
    accentTextColor = WHITE_TEXT_COLOR;
  } else {
    // eslint-disable-next-line no-lonely-if
    if (isDarkTheme) {
      accentBgColor = 'transparent';
      accentTextColor = WHITE_TEXT_COLOR;
    } else {
      accentBgColor = 'var(--sops-white-to-white)';
      accentTextColor = DARK_TEXT_COLOR;
    }
  }

  return { accentBgColor, accentTextColor };
}
